import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  BarChart3, DollarSign,
  Shield,
  Building, Library, GraduationCap,
  ClipboardList, BookOpen, ShieldCheck, TrendingDown,
} from 'lucide-react';
import { useTabPersistence } from '../../../hooks/usePersistence';

// Tab Components
import CourseRequestsTab from './components/CourseRequestsTab';
import TrainingProvidersTab from './components/TrainingProvidersTab';
import EducationInstitutionsTab from './components/EducationInstitutionsTab';
import BudgetManagementTab from './components/BudgetManagementTab';
import CourseLibraryTab from './components/CourseLibraryTab';
import TrainingRecordsTab from './components/TrainingRecordsTab';
import CertComplianceTab from './components/CertComplianceTab';
import LevyOptimizationTab from './components/LevyOptimizationTab';
import BondManagementTab from './components/BondManagementTab';

const VALID_TABS = [
  'training-records',
  'cert-compliance',
  'levy-optimization',
  'course-requests',
  'course-library',
  'training-providers',
  'education-institutions',
  'budget-overview',
  'bond-management',
  'admin-reports',
];

const LearningDevelopmentAdmin = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useTabPersistence(
    'learning-development',
    'training-records',
    VALID_TABS,
  );

  // `view=legacy` is retired. Keep only request deep links on Course Requests.
  useEffect(() => {
    const hasRetiredView = searchParams.has('view');
    const hasStaleDetail = activeTab !== 'course-requests' && searchParams.has('id');
    if (!hasRetiredView && !hasStaleDetail) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('view');
        if (activeTab !== 'course-requests') next.delete('id');
        return next;
      },
      { replace: true },
    );
  }, [activeTab, searchParams, setSearchParams]);

  const adminTabs = [
    { id: 'training-records', name: 'Training Records', icon: ClipboardList, description: 'Company-sponsored training history by budget year' },
    { id: 'cert-compliance', name: 'Cert Compliance', icon: ShieldCheck, description: 'Mandatory certification rules and live compliance monitoring' },
    { id: 'levy-optimization', name: 'Levy Optimization', icon: TrendingDown, description: 'Workers at risk of losing their lowered levy + upgrade opportunities' },
    { id: 'course-requests', name: 'Course Requests', icon: GraduationCap, description: 'Review training needs, arrange bookings, and record outcomes' },
    { id: 'course-library', name: 'Course Library', icon: Library, description: 'Master course directory and provider mappings' },
    { id: 'training-providers', name: 'Training Providers', icon: Building, description: 'Manage training provider directory and compliance' },
    { id: 'education-institutions', name: 'Education Institutions', icon: GraduationCap, description: 'Manage education institution library and credentials' },
    { id: 'budget-overview', name: 'Budget Management', icon: DollarSign, description: 'Department training budget tracking' },
    { id: 'bond-management', name: 'Bond Management', icon: Shield, description: 'Sponsorship bond agreements tracking' },
    { id: 'admin-reports', name: 'Reports & Analytics', icon: BarChart3, description: 'Training program analytics' },
  ];

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'training-records':
        return <TrainingRecordsTab />;
      case 'cert-compliance':
        return <CertComplianceTab />;
      case 'levy-optimization':
        return <LevyOptimizationTab />;
      case 'course-requests':
        return <CourseRequestsTab />;
      case 'course-library':
        return <CourseLibraryTab />;
      case 'training-providers':
        return <TrainingProvidersTab />;
      case 'education-institutions':
        return <EducationInstitutionsTab />;
      case 'budget-overview':
        return <BudgetManagementTab />;
      case 'bond-management':
        return <BondManagementTab />;
      case 'admin-reports':
        return (
          <div className="text-center py-12">
            <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Reports & Analytics</h3>
            <p className="text-gray-500">Training program analytics and insights coming soon...</p>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-1 min-w-0">
          <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
          </div>
          <h1 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">Learning & Development</h1>
        </div>
        <p className="text-xs sm:text-sm text-gray-500 ml-11 sm:ml-12">
          Manage course requests, training records, providers, and budgets
        </p>
      </div>

      <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth mb-4 md:mb-6">
        {adminTabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`tab-btn-admin ${isActive ? 'active' : ''}`}
            >
              <Icon className="w-4 h-4" />
              {tab.name}
            </button>
          );
        })}
      </div>

      <div className="card">
        <div className="card-content">
          {renderActiveTab()}
        </div>
      </div>
    </div>
  );
};

export default LearningDevelopmentAdmin;
